import moment from 'moment-timezone';

import {
  CancellationPolicy as ApiCancellationPolicy,
  DynamicPricing,
  ItineraryEvent,
  LocationWithTime,
  Product,
  ProductPricing,
  ProductPromotion,
  Recurrence,
  Service,
  Unit,
  Weekday,
} from 'models/product';
import { PromotionType, FamilyDiscount, GroupDiscount } from 'models/promotion';
import { RelativeDateTime } from 'models/commons';
import { getProductCurrency } from 'lib/util/getProductCurrency';
import { uniqBy } from 'lodash';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ProductShape {
  id: string;
  name: string;
  description: string;
  mediaURLs: { url: string; type: 'IMAGE' | 'VIDEO' }[];
  tags: string[];
  operatingWeekdays: Weekday[];
  durationInHours: string;
  startTimes: StartTime[];
  priceSchedules: PriceSchedule[];
  promotionSummaries: PromotionSummary[];
  highlights: string[];
  addOns: ServiceShape[];
  transportations: ServiceShape[];
  inclusions: string[];
  exclusions: string[];
  restrictions: string[];
  requirements: string[];
  whatToBring: string[];
  otherInfo: string[];
  minimumParticipants: number;
  cancellationPolicies: CancellationPolicy[];
  schedule: ScheduleEventShape[];
  pickupLocations: LocationShape[];
  dropoffLocations: LocationShape[];
  checkinLocations: LocationShape[];
  checkoutLocations: LocationShape[];
  bestDiscountGross: string;
  bestDiscountPercent: number;
  totalBooked: number;
  currency: string;
  reviewCount: number;
  reviewRating: number;
  agentGuestBookingAllowedDaysBefore: number | null;
  haveMultipleApplyDiscount: boolean;
  shouldShowPricingOnCalendar: boolean;
  automaticPriceAdjustmentSettings: {
    schedules: {
      participationDateRanges: DateRange[];
      participationDaysOfWeek: ('SUN' | 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT')[];
      appliesFromParticipationRelativeDateTime: RelativeDateTime;
      appliesUntilParticipationRelativeDateTime: RelativeDateTime;
    }[];
  } | null;
}

export type RelativeDeadline =
  | {
      deadlineType: 'ETERNAL';
    }
  | {
      deadlineType: 'HOUR';
      hoursBefore: number;
    }
  | {
      deadlineType: 'DAY';
      daysBefore: number;
      timeOfDay: string;
    };

type CancellationFee =
  | {
      feeType: 'FIXED';
      feeFixed: string;
    }
  | {
      feeType: 'PERCENT';
      feePercent: string;
    };
export type CancellationPolicy = RelativeDeadline & CancellationFee;

export interface PriceSchedule {
  startDate: string;
  endDate: string;
  weekdays: Weekday[];
  prices: Price[];
  original: ProductPricing;
}

export interface Price {
  unitTitle: string;
  minimumAge?: number;
  maximumAge?: number;
  amount: string;

  amountAfterChargeOrDiscount: string;
  deltaType: 'CHARGE' | 'DISCOUNT' | '';

  deltaAmount: string;
  deltaPercent: string;
}

export interface ServiceShape {
  name: string;
  description: string;
  type: 'FREE' | 'PAID';
  pricesSummary: Price[];
}

export interface ScheduleEventShape {
  name: string;
  description: string;
  startTimeRelative?: string;
  endTimeRelative?: string;
}

export interface LocationShape {
  name: string;
  googlePlaceID: string;
  description: string;
  timeRelative?: string;
  dedicatedStartTimeInfoItems: DedicatedStartTimeInfoItem[];
}

export interface DedicatedStartTimeInfoItem {
  timeSlotKey: string;
  timeRelative: string;
}

export interface StartTime {
  hhMm: string;
  startDate: string;
  timeSlotKey: string;
  description: string;
}

export interface PromotionSummary {
  id: string;
  participationDateRanges: DateRange[];
  participationDaysOfWeek: Weekday[];
  bookedDateLocalFrom: string;
  bookedDateLocalTo: string;
  type: PromotionType;
  earlyBirdDeadline: RelativeDateTime;
  lastMinuteStart: RelativeDateTime;
  groupDiscount: GroupDiscount;
  familyDiscount: FamilyDiscount;
  prices: Price[];
}

export const toProductShape = (product: Product): ProductShape => {
  const mediaURLs: { url: string; type: 'IMAGE' | 'VIDEO' }[] = [];
  for (const mediaItem of product.media || []) {
    if (mediaItem.type === 'IMAGE' || mediaItem.type === 'VIDEO') {
      mediaURLs.push({
        url: mediaItem.url,
        type: mediaItem.type,
      });
    }
  }

  const priceSchedules: PriceSchedule[] = (product.pricing ?? [])
    .filter(
      (priceSchedule) =>
        !priceSchedule.end_date_local ||
        priceSchedule.end_date_local >
          moment()
            .tz(product.start_timezone ?? '')
            .format('YYYY-MM-DD')
    )
    .map((priceSchedule) => ({
      startDate: priceSchedule.start_date_local ?? '',
      endDate: priceSchedule.end_date_local ?? '',
      weekdays: priceSchedule.days_of_week ?? [],
      prices: getPrices(priceSchedule.units),
      original: priceSchedule,
    }));

  let promotionSummaries: PromotionSummary[] = [];
  for (const priceSchedule of priceSchedules) {
    const promotions = findActiveAndFuturePromotions(
      priceSchedule.original.promotions ?? [],
      product.start_timezone
    );

    for (const promotion of promotions) {
      promotionSummaries.push(toPromotionSummary(promotion, priceSchedule.original.units));
    }
  }

  promotionSummaries = uniqBy(promotionSummaries, 'id');

  const cancellationPolicies =
    product?.cancellation_policies != null && product.cancellation_policies.length > 0
      ? product.cancellation_policies
          .filter((cancellationPolicy) => !isDefaultCancellationPolicy(cancellationPolicy))
          .map((cancellationPolicy) => convertToCancellationPolicy(cancellationPolicy))
      : [];

  const hasPerParticipantPricing =
    product?.pricing == null ||
    product?.pricing.some((priceRule) => {
      if (priceRule.units == null) {
        return false;
      }

      return priceRule.units.some((unit) => unit.method === 'PER_PARTICIPANT');
    });
  const isPackage =
    (product?.shared_allotment_references?.package_component_product_ids ?? []).length > 0;
  const isSharedAllotment = Boolean(product?.shared_allotment_references?.parent_product_id);
  const isPassthrough = Boolean(product?.shared_allotment_references?.passthrough_base_product_id);
  const showMinimumParticipants =
    hasPerParticipantPricing && !isPackage && !isSharedAllotment && !isPassthrough;

  return {
    id: product.id || '',
    name: product.product_name || '',
    description: product.description || '',
    mediaURLs,
    tags: product.product_tags || [],
    operatingWeekdays: product.operating_days_of_week || [],
    durationInHours: getDurationInHoursFromRecurrence(product.recurrence || []),
    priceSchedules,
    promotionSummaries,
    highlights: product.highlights || [],
    addOns: (product.add_ons || []).map(toServiceShape),
    transportations: (product.transportations || []).map(toServiceShape),
    inclusions: product.inclusions || [],
    exclusions: product.exclusions || [],
    restrictions: product.restrictions || [],
    requirements: product.requirements || [],
    whatToBring: product.what_to_bring || [],
    otherInfo: product.other_notes || [],
    minimumParticipants: showMinimumParticipants
      ? (product.minimum_participant_count && product.minimum_participant_count.value) ?? 0
      : 0,
    schedule: (product.itinerary || []).map(toScheduleEventShape),
    startTimes: getStartTimesFromRecurrence(product.recurrence || []),
    pickupLocations: (product.pickup || []).map(toLocationShape),
    dropoffLocations: (product.dropoff || []).map(toLocationShape),
    checkinLocations: (product.checkin || []).map(toLocationShape),
    checkoutLocations: (product.checkout || []).map(toLocationShape),
    bestDiscountGross: product.best_discount_gross || '',
    bestDiscountPercent: product.best_discount_percent || 0,
    totalBooked: product.total_booked || 0,
    cancellationPolicies,
    currency: getProductCurrency(product) ?? '',
    reviewCount: product.review_count ?? 0,
    reviewRating: product.review_rating ?? 0,
    agentGuestBookingAllowedDaysBefore:
      product?.agent_guest_booking_period_settings?.booking_allowed_days_before_participation ??
      null,
    haveMultipleApplyDiscount: product?.have_multiple_apply_discount ?? false,
    shouldShowPricingOnCalendar: product?.should_show_pricing_on_bw_calendar?.value ?? false,
    automaticPriceAdjustmentSettings: product.automatic_price_adjustment_settings
      ? {
          schedules:
            product.automatic_price_adjustment_settings?.schedules?.map((schedule) => ({
              participationDateRanges: (schedule.participation_date_ranges || []).map(
                (dateRange) => ({
                  startDate: dateRange.start_date_local,
                  endDate: dateRange.end_date_local,
                })
              ),
              participationDaysOfWeek: schedule.participation_days_of_week || [],
              appliesFromParticipationRelativeDateTime: schedule.applies_from_participation_relative_date_time || {
                description: '',
                type: 'DAY',
                count: undefined,
                time_local: '',
              },
              appliesUntilParticipationRelativeDateTime: schedule.applies_until_participation_relative_date_time || {
                description: '',
                type: 'DAY',
                count: undefined,
                time_local: '',
              },
            })) || [],
        }
      : null,
  };
};

export const getPrices = (
  units: Unit[],
  promotion?: ProductPromotion,
  dynamicPricingUnits?: DynamicPricing[]
): Price[] => {
  return units.map((unit) => {
    const adjustedUnitPrice =
      promotion &&
      promotion.adjusted_unit_prices &&
      promotion.adjusted_unit_prices.find(
        (p) =>
          p.unit_price_method === unit.method &&
          (p.unit_price_method !== 'PER_PARTICIPANT' ||
            p.guest_type_key === (unit.guest_type && unit.guest_type.key))
      );
    let amountAfterChargeOrDiscount = unit.gross;
    let deltaType: 'CHARGE' | 'DISCOUNT' | '' = '';
    let deltaAmount = '';
    let deltaPercent = '';
    if (adjustedUnitPrice) {
      amountAfterChargeOrDiscount = adjustedUnitPrice.adjusted_gross;
      deltaType = adjustedUnitPrice.delta_type;
      deltaAmount = adjustedUnitPrice.delta_fixed_gross;
      deltaPercent = adjustedUnitPrice.delta_percent ? `${adjustedUnitPrice.delta_percent}%` : '';
    }

    const dynamicPricingAdjustedUnitPrice =
      dynamicPricingUnits &&
      dynamicPricingUnits.length > 0 &&
      dynamicPricingUnits[0].prices &&
      dynamicPricingUnits[0].prices.find(
        (p) =>
          p.unit_price_method === unit.method &&
          (p.unit_price_method !== 'PER_PARTICIPANT' ||
            p.guest_type_key === (unit.guest_type && unit.guest_type.key))
      );
    if (dynamicPricingAdjustedUnitPrice) {
      amountAfterChargeOrDiscount = dynamicPricingAdjustedUnitPrice.adjusted_gross;
      deltaType = dynamicPricingAdjustedUnitPrice.delta_type;
      deltaAmount = dynamicPricingAdjustedUnitPrice.delta_fixed_gross;
      deltaPercent = dynamicPricingAdjustedUnitPrice.delta_percent
        ? `${dynamicPricingAdjustedUnitPrice.delta_percent}%`
        : '';
    }

    switch (unit.method) {
      case 'PER_PARTICIPANT':
        return {
          unitTitle: (unit.guest_type && unit.guest_type.title) || '',
          minimumAge: unit.guest_type?.minimum_age,
          maximumAge: unit.guest_type?.maximum_age,
          amount: unit.gross,
          amountAfterChargeOrDiscount,
          deltaType,
          deltaAmount,
          deltaPercent,
        };
      case 'PER_GROUP':
        return {
          unitTitle: getGroupPriceTitle(unit),
          amount: unit.gross,
          amountAfterChargeOrDiscount,
          deltaType,
          deltaAmount,
          deltaPercent,
        };
      case 'PER_BOOKING':
        return {
          unitTitle: '',
          amount: unit.gross,
          amountAfterChargeOrDiscount,
          deltaType,
          deltaAmount,
          deltaPercent,
        };
      default:
        return {
          unitTitle: unit.title,
          amount: unit.gross,
          amountAfterChargeOrDiscount,
          deltaType,
          deltaAmount,
          deltaPercent,
        };
    }
  });
};

export const isDefaultCancellationPolicy = (cancellationPolicy: ApiCancellationPolicy): boolean => {
  return (
    cancellationPolicy.relative_date.type === 'HOUR' &&
    !cancellationPolicy.relative_date.count &&
    !cancellationPolicy.charge_fixed &&
    cancellationPolicy.charge_percent === 100
  );
};

export const convertToCancellationPolicy = (
  productCancellationPolicy: ApiCancellationPolicy
): CancellationPolicy => {
  const chargeFixed = productCancellationPolicy.charge_fixed;
  const feeFixed = chargeFixed && chargeFixed.length > 3 ? chargeFixed.substring(3) : undefined;

  return productCancellationPolicy.relative_date.type === 'ETERNAL'
    ? {
        deadlineType: 'ETERNAL',
        ...(feeFixed
          ? {
              feeType: 'FIXED',
              feeFixed,
            }
          : {
              feeType: 'PERCENT',
              feePercent: `${productCancellationPolicy.charge_percent ?? 0}`,
            }),
      }
    : productCancellationPolicy.relative_date.type === 'HOUR'
    ? {
        deadlineType: 'HOUR',
        hoursBefore: productCancellationPolicy.relative_date?.count ?? 0,
        ...(feeFixed
          ? {
              feeType: 'FIXED',
              feeFixed,
            }
          : {
              feeType: 'PERCENT',
              feePercent: `${productCancellationPolicy.charge_percent ?? 0}`,
            }),
      }
    : {
        deadlineType: 'DAY',
        daysBefore: productCancellationPolicy.relative_date?.count ?? 0,
        timeOfDay: productCancellationPolicy.relative_date.time_local || '0:00',
        ...(feeFixed
          ? {
              feeType: 'FIXED',
              feeFixed,
            }
          : {
              feeType: 'PERCENT',
              feePercent: `${productCancellationPolicy.charge_percent ?? 0}`,
            }),
      };
};

const getGroupPriceTitle = (unit: Unit): string => {
  const guestTypeCounts: { [guestTypeTitle: string]: number } = {};

  for (const guestType of unit.group_guest_types) {
    guestTypeCounts[guestType.title] = (guestTypeCounts[guestType.title] || 0) + 1;
  }

  const unitBreakdownText = Object.keys(guestTypeCounts)
    .map((guestTypeTitle: string) => `${guestTypeTitle} x ${guestTypeCounts[guestTypeTitle]}`)
    .join(',');
  return `${unit.title} (${unitBreakdownText})`;
};

const getDurationInHours = (durString: string): string => {
  const match = /(-?[0-9]+):([0-9]+)/.exec(durString);
  if (!match) {
    return '0';
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  const isNegative = Math.sign(hours) === -1 || Object.is(hours, -0);
  const hoursAsNumber = isNegative ? -hours - minutes / 60.0 : hours + minutes / 60.0;

  if (!hoursAsNumber) {
    return '';
  }

  return Number.isInteger(hoursAsNumber) ? `${hoursAsNumber}` : hoursAsNumber.toFixed(1);
};

const toServiceShape = (service: Service): ServiceShape => {
  return {
    name: service.title || '',
    description: service.description || '',
    type: service.service_type === 'FREE' ? 'FREE' : 'PAID',
    pricesSummary: service.pricing ? getPrices(service.pricing) : [],
  };
};

const toScheduleEventShape = (itineraryEvent: ItineraryEvent): ScheduleEventShape => {
  return {
    startTimeRelative: itineraryEvent.start_time_relative,
    endTimeRelative: itineraryEvent.end_time_relative,
    name: itineraryEvent.title || '',
    description: itineraryEvent.description || '',
  };
};

const toLocationShape = (locationWithTime: LocationWithTime): LocationShape => {
  return {
    name: locationWithTime.location_name || '',
    googlePlaceID: locationWithTime.google_place_id || '',
    description: locationWithTime.location_description || '',
    timeRelative: locationWithTime.time_relative || '',
    dedicatedStartTimeInfoItems: (locationWithTime.dedicated_start_time_info || []).map(
      (dedicatedStartTimeInfo) => ({
        timeSlotKey: dedicatedStartTimeInfo.time_slot_key || '',
        timeRelative:
          (dedicatedStartTimeInfo.time_relative && dedicatedStartTimeInfo.time_relative) || '',
      })
    ),
  };
};

const findActiveAndFuturePromotions = (
  promotions: ProductPromotion[],
  timezone: string
): ProductPromotion[] => {
  const nowDate = moment.tz(timezone).format('YYYY-MM-DD');

  return promotions.filter((promotion) => {
    let latestStartDateLocalTo = '';
    for (const dateRange of promotion.participation_date_ranges) {
      if (dateRange.end_date_local && dateRange.end_date_local > latestStartDateLocalTo) {
        latestStartDateLocalTo = dateRange.end_date_local;
      }
    }

    return (
      (!promotion.booked_date_local_to || promotion.booked_date_local_to >= nowDate) &&
      (!latestStartDateLocalTo || latestStartDateLocalTo > nowDate)
    );
  });
};

export const getStartTimesFromRecurrence = (recurrence: Recurrence[]): StartTime[] => {
  const startTimesByHhMm: { [hhMm: string]: StartTime } = {};

  const today = moment().format('YYYY-MM-DD');

  for (const rule of recurrence) {
    const allEndDates = [
      rule.end_date_local,
      ...(rule.additional_date_ranges ?? []).map((dateRange) => dateRange.end_date_local),
    ];

    if (!allEndDates.some((endDate) => endDate >= today)) {
      // Skip recurrence rules that have already passed
      continue;
    }

    if (!rule.start_times) {
      continue;
    }

    let earliestStartDate = '';
    const allStartDates = [
      rule.start_date_local,
      ...(rule.additional_date_ranges ?? []).map((dateRange) => dateRange.start_date_local),
    ];

    if (allStartDates.length > 0) {
      earliestStartDate = allStartDates[0];
      for (const date of allStartDates) {
        if (date < earliestStartDate) {
          earliestStartDate = date;
        }
      }
    }

    for (const ruleStartTime of rule.start_times) {
      if (!ruleStartTime.start_time_local) {
        continue;
      }

      const startTimeLocal = moment(ruleStartTime.start_time_local, 'H:mm').format('H:mm');

      const existingStartTime = startTimesByHhMm[startTimeLocal];

      if (existingStartTime && existingStartTime.startDate < earliestStartDate) {
        continue;
      }

      startTimesByHhMm[startTimeLocal] = {
        hhMm: startTimeLocal,
        startDate: earliestStartDate,
        timeSlotKey: ruleStartTime.time_slot_key || '',
        description: ruleStartTime.description || '',
      };
    }
  }

  const startTimes = Object.values(startTimesByHhMm);
  startTimes.sort((startTime1, startTime2) => {
    if (today < startTime2.startDate && startTime1.startDate < startTime2.startDate) {
      return -1;
    }
    if (today < startTime1.startDate && startTime1.startDate > startTime2.startDate) {
      return 1;
    }
    return moment(startTime1.hhMm, 'h:mm').valueOf() - moment(startTime2.hhMm, 'h:mm').valueOf();
  });

  return startTimes;
};

export const getDurationInHoursFromRecurrence = (recurrence: Recurrence[]): string => {
  const today = moment().format('YYYY-MM-DD');

  const activeRule = recurrence.find((rule) => {
    return (
      (rule.start_date_local <= today && rule.end_date_local >= today) ||
      rule.additional_date_ranges.some(
        (dateRange) => dateRange.start_date_local <= today && dateRange.end_date_local >= today
      )
    );
  });

  if (activeRule && activeRule.start_times?.length) {
    return getDurationInHours(activeRule.start_times[0].duration);
  }

  const futureRule = recurrence.find((rule) => {
    return (
      rule.end_date_local >= today ||
      rule.additional_date_ranges.some((dateRange) => dateRange.end_date_local >= today)
    );
  });

  if (futureRule && futureRule.start_times?.length) {
    return getDurationInHours(futureRule.start_times[0].duration);
  }

  return '';
};

export const toPromotionSummary = (
  promotion: ProductPromotion,
  units: Unit[]
): PromotionSummary => ({
  id: promotion.id || '',
  participationDateRanges:
    promotion.participation_date_ranges?.map((dateRange) => ({
      startDate: dateRange.start_date_local || '',
      endDate: dateRange.end_date_local || '',
    })) || [],
  participationDaysOfWeek: promotion.participation_days_of_week || [],
  bookedDateLocalFrom: promotion.booked_date_local_from || '',
  bookedDateLocalTo: promotion.booked_date_local_to,
  type: promotion.type,
  earlyBirdDeadline: promotion.early_bird_deadline,
  lastMinuteStart: promotion.last_minute_start,
  groupDiscount: promotion.group_discount,
  familyDiscount: promotion.family_discount,
  prices: getPrices(units, promotion) || [],
});
